/**
 * A time of day, picked off a clock face.
 *
 * This is the control a clock face is actually FOR. A backup window opens at
 * two in the morning and closes at six, and both of those are positions on a
 * dial - which is why the RPO, a duration, gets a list picker instead.
 *
 * Twelve-hour face with AM/PM rather than a 24-hour double ring. Every phone
 * does it this way, the people setting these windows are American MSPs who
 * think in AM and PM, and "02:00" and "14:00" are one keystroke apart on a
 * keyboard but half a face apart here.
 *
 * IT CLOSES ITSELF once the minutes are set. A picker you have to dismiss is a
 * picker that stays open over the field below it.
 */

import { useState } from 'react';
import { Popover } from './Popover';
import { formatClock, from12h, hour12At, minuteAt, to12h } from './timeMath';

const SIZE = 210;
const C = SIZE / 2;
const R = 84;

const pos = (i: number, r: number) => ({
  x: C + r * Math.sin((i * 30 * Math.PI) / 180),
  y: C - r * Math.cos((i * 30 * Math.PI) / 180),
});

export function TimeField({ value, onChange, disabled, allowClear, label }: {
  /** Minute of day, 0..1439. Null means not set, where the caller allows it. */
  value: number | null;
  onChange: (minuteOfDay: number | null) => void;
  disabled?: boolean;
  allowClear?: boolean;
  /** For the accessible name, e.g. "Backup window opens". */
  label: string;
}) {
  return (
    <Popover label={formatClock(value)} title={label} disabled={disabled}>
      {(close) => (
        <ClockFace
          value={value}
          label={label}
          allowClear={allowClear}
          onDone={(v) => { onChange(v); close(); }}
          onPartial={onChange}
        />
      )}
    </Popover>
  );
}

function ClockFace({ value, label, allowClear, onDone, onPartial }: {
  value: number | null;
  label: string;
  allowClear?: boolean;
  /** The answer is complete - commit and close. */
  onDone: (minuteOfDay: number | null) => void;
  /** The hour changed but the minutes have not been given yet. */
  onPartial: (minuteOfDay: number) => void;
}) {
  const start = to12h(value ?? 0);
  const [h12, setH12] = useState(start.h12);
  const [m, setM] = useState(start.m);
  const [pm, setPm] = useState(start.pm);
  const [step, setStep] = useState<'hours' | 'minutes'>('hours');

  function click(e: React.MouseEvent<SVGSVGElement>) {
    const r = e.currentTarget.getBoundingClientRect();
    // Scaled to the viewBox so this stays right whatever the CSS size is.
    const dx = ((e.clientX - r.left) / r.width) * SIZE - C;
    const dy = ((e.clientY - r.top) / r.height) * SIZE - C;

    if (step === 'hours') {
      const nh = hour12At(dx, dy);
      setH12(nh);
      onPartial(from12h(nh, m, pm));
      // Straight on to the minutes, the way every clock picker does it: the
      // second half of the answer is the half people forget to give.
      setStep('minutes');
    } else {
      const nm = minuteAt(dx, dy);
      setM(nm);
      onDone(from12h(h12, nm, pm));
    }
  }

  const angle = step === 'hours' ? (h12 % 12) * 30 : m * 6;
  const hand = {
    x: C + R * Math.sin((angle * Math.PI) / 180),
    y: C - R * Math.cos((angle * Math.PI) / 180),
  };

  return (
    <div className="clock">
      <div className="clock-readout">
        <button
          type="button"
          className={'dial-part' + (step === 'hours' ? ' on' : '')}
          onClick={() => setStep('hours')}
        >
          {h12}
        </button>
        <span className="dial-colon">:</span>
        <button
          type="button"
          className={'dial-part' + (step === 'minutes' ? ' on' : '')}
          onClick={() => setStep('minutes')}
        >
          {String(m).padStart(2, '0')}
        </button>
        <div className="clock-ampm">
          {([false, true] as const).map((p) => (
            <button
              key={String(p)}
              type="button"
              className={'clock-mer' + (pm === p ? ' on' : '')}
              onClick={() => { setPm(p); onPartial(from12h(h12, m, p)); }}
            >
              {p ? 'PM' : 'AM'}
            </button>
          ))}
        </div>
      </div>

      <svg
        className="dial"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        width={SIZE}
        height={SIZE}
        onClick={click}
        role="img"
        aria-label={`${label}, ${formatClock(from12h(h12, m, pm))}`}
      >
        <circle cx={C} cy={C} r={99} className="dial-face" />
        <line x1={C} y1={C} x2={hand.x} y2={hand.y} className="dial-hand" />
        <circle cx={hand.x} cy={hand.y} r={16} className="dial-knob" />
        <circle cx={C} cy={C} r={3} className="dial-pin" />

        {Array.from({ length: 12 }, (_, i) => {
          const n = step === 'hours' ? (i === 0 ? 12 : i) : i * 5;
          const sel = step === 'hours' ? h12 === n : m === n;
          const p = pos(i, R);
          return (
            <text key={i} x={p.x} y={p.y} className={'dial-num' + (sel ? ' sel' : '')}>
              {step === 'hours' ? n : String(n).padStart(2, '0')}
            </text>
          );
        })}
      </svg>

      <div className="clock-foot">
        <span className="dial-hint">{step === 'hours' ? 'pick the hour' : 'pick the minutes'}</span>
        {allowClear && (
          <button type="button" className="btn-link" onClick={() => onDone(null)}>Clear</button>
        )}
      </div>
    </div>
  );
}
